import { useEffect, useRef } from 'react';
import { t } from '../lib/i18n';

/**
 * A dialog, using the element the browser already has (Phase 146).
 *
 * `<dialog showModal>` rather than a div with a z-index: it traps focus, it closes on Escape, it
 * makes the rest of the page inert and it draws its own backdrop — four things every hand-rolled
 * modal in every codebase gets wrong at least once, and gets wrong for the people who need them
 * most.
 *
 * The pattern existed twice already (the account dialog, the new conversation dialog) and was
 * copied both times; this is the same thing with a name, so the fifth one does not copy it again.
 */

export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  wide,
}: {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: React.ReactNode;
  /** The row of buttons. Separate so it can stay put while the body scrolls. */
  footer?: React.ReactNode;
  wide?: boolean;
}): React.JSX.Element | null {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = dialog.current;

    if (!element) return;

    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={dialog}
      className={`confirm modal${wide ? ' modal--wide' : ''}`}
      onCancel={(event) => {
        // Escape closes it through the same path as the × , so a caller has one thing to handle.
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
    >
      <div className="modal__head">
        <span className="cell-stack">
          <strong>{title}</strong>
          {description && <span className="muted small">{description}</span>}
        </span>

        <button
          type="button"
          className="button button--quiet button--compact"
          aria-label={t('common.close')}
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <div className="modal__body">{children}</div>

      {footer && <div className="modal__foot">{footer}</div>}
    </dialog>
  );
}
